import { useCallback } from "react"
import { useSignal } from "@preact/signals-react"
import { useToastSeeker } from "@ui-seeker/app"
import { deleteSubmit, getSubmit, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS, SERVICES_TYPE } from "@constants"
import {
	acrissGroupData,
	acrissGroupFormData,
	isEditAcrissGroupDrawer,
	isLoadingAcrissGroupData,
} from "Pages/OneWay/AcrissGroups/@signals"


export default function useAcrissGroups(rowData = {}) {
	const { showToast } = useToastSeeker()
	const isLoading = useSignal(false)

	const getAcrissGroupData = useCallback(() => {
		getSubmit(`${BACKEND_APIS.CRUD}/user/${SERVICES_TYPE.ACRISS_GROUP}`)
			.then(data => acrissGroupData.value = data)
			.catch(() => {
				showToast.error()
				acrissGroupData.value = []
			}).finally(() => isLoadingAcrissGroupData.value = false)
	}, [])

	const handleSetFormData = useCallback(() => acrissGroupFormData.value = {
		id: rowData.id || null,
		service_type: SERVICES_TYPE.ACRISS_GROUP,
		// No placeholder name here, unlike the other group CRUDs: the name is the header
		// the analyzer prints, so it has to be written on purpose.
		name: rowData.name || "",
		acriss: rowData.acriss || [],
	}, [JSON.stringify(rowData)])

	const handleChangeFormData = useCallback((newValue, prop) => acrissGroupFormData.value = {
		...acrissGroupFormData.value,
		[prop]: newValue,
	}, [])

	const handleSubmitFormData = useCallback(() => {
		isLoading.value = true
		postSubmit(
			`${BACKEND_APIS.CRUD}/${!isEditAcrissGroupDrawer.value ? 'add' : 'update'}`,
			acrissGroupFormData.value
		).then(res => {
			if (isEditAcrissGroupDrawer.value) {
				acrissGroupData.value = acrissGroupData.value.map(
					item => item.id === acrissGroupFormData.value.id ? res : item
				)
			} else {
				acrissGroupData.value = [...acrissGroupData.value, res]
			}
			acrissGroupFormData.value = {}
		}).catch(() => showToast.error())
			.finally(() => isLoading.value = false)
	}, [])

	const handleDeleteRow = useCallback(() => showToast.confirm(
		`Are you sure you want to delete this row? This action cannot be undone.`,
		{
			onSubmit: () => {
				isLoading.value = true
				deleteSubmit(`${BACKEND_APIS.CRUD}/delete/${SERVICES_TYPE.ACRISS_GROUP}?id=${rowData.id}`)
					.then(() => {
						acrissGroupData.value = acrissGroupData.value.filter(item => item.id !== rowData.id)
					}).catch(() => showToast.error())
					.finally(() => isLoading.value = false)
			}
		}
	), [JSON.stringify(rowData)])

	return {
		isClicked: rowData.id === acrissGroupFormData.value?.id,
		isLoading: isLoading.value,
		getAcrissGroupData,
		handleSetFormData,
		handleChangeFormData,
		handleSubmitFormData,
		handleDeleteRow,
	}
}
